import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { HelpCircle, ArrowLeft, Send, XCircle, Clock, CheckCircle, MessageCircle, Search, Filter, ChevronDown, RefreshCw, Paperclip, Download, Image, X } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import FilterSelect from '../components/FilterSelect';
import FilterInput from '../components/FilterInput';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';

const AdminSupport = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // Default to all tickets
  const [searchInput, setSearchInput] = useState(''); // What user types
  const [search, setSearch] = useState(''); // Debounced search value
  const { id } = useParams();
  const navigate = useNavigate();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchTickets();
  }, [filter, search]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      if (search) params.append('search', search);
      
      const response = await api.get(`/admin/support/tickets?${params.toString()}`);
      setTickets(response.data);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
      in_progress: { bg: 'bg-amber-100', text: 'text-amber-700', icon: MessageCircle },
      closed: { bg: 'bg-slate-100', text: 'text-slate-600', icon: CheckCircle }
    };
    const style = styles[status] || styles.open;
    const Icon = style.icon;
    return (
      <span className={`inline-flex items-center gap-1 ${style.bg} ${style.text} px-2 py-1 rounded-full text-xs font-medium`}>
        <Icon className="w-3 h-3" />
        {status.replace('_', ' ')}
      </span>
    );
  };

  // If viewing single ticket
  if (id) {
    return <AdminTicketDetail ticketId={id} />;
  }

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-900"></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Support Tickets</h1>
            <p className="text-slate-600">Manage user support requests</p>
          </div>
          <button
            onClick={fetchTickets}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-xl transition-all hover:opacity-90"
            style={{ backgroundColor: BRAND_BLUE }}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6 flex flex-wrap gap-4 items-center shadow-sm">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-600">Filters:</span>
          </div>

          {/* Search */}
          <FilterInput
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by subject, user, or content..."
            className="flex-1 min-w-[200px] max-w-md"
            data-testid="search-tickets"
          />

          {/* Status Filter */}
          <FilterSelect
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            options={[
              { value: 'open', label: 'Open' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'closed', label: 'Closed' },
              { value: 'all', label: 'All Statuses' }
            ]}
            data-testid="filter-status"
          />

          {/* Results count */}
          <span className="text-sm text-gray-500">
            {tickets.length} ticket{tickets.length !== 1 ? 's' : ''} found
          </span>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Ticket</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">User</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Subject</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Status</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div>
                    </div>
                  </td>
                </tr>
              ) : tickets.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                    No tickets found.
                  </td>
                </tr>
              ) : (
                tickets.map((ticket) => (
                  <tr
                    key={ticket.id}
                    onClick={() => navigate(`/admin/support/${ticket.id}`)}
                    className="hover:bg-slate-50 cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm text-slate-500">#{ticket.id?.slice(-8)}</span>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{ticket.user_email}</td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-slate-800">{ticket.subject}</p>
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(ticket.status)}</td>
                    <td className="px-6 py-4 text-slate-600">
                      {new Date(ticket.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Sidebar>
  );
};

const isImageFile = (filename) => /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(filename);

const AdminAttachmentDisplay = ({ attachment, isAdminMsg }) => {
  if (!attachment) return null;
  return (
    <div className="mt-2">
      {isImageFile(attachment.file_name) ? (
        <a href={attachment.file_url} target="_blank" rel="noopener noreferrer">
          <img
            src={attachment.file_url}
            alt={attachment.file_name}
            className="max-w-xs max-h-48 rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition"
          />
          <span className={`flex items-center gap-1 text-xs mt-1 ${isAdminMsg ? 'text-emerald-200' : 'text-gray-500'}`}>
            <Image className="w-3 h-3" />
            {attachment.file_name}
          </span>
        </a>
      ) : (
        <a
          href={attachment.file_url}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center gap-2 text-xs rounded px-2 py-1 w-fit ${
            isAdminMsg ? 'text-emerald-100 hover:text-white bg-emerald-700/30' : 'text-blue-600 hover:text-blue-800 bg-white/60'
          }`}
        >
          <Paperclip className="w-3 h-3" />
          {attachment.file_name}
          <Download className="w-3 h-3" />
        </a>
      )}
    </div>
  );
};

// Admin Ticket Detail Component
const AdminTicketDetail = ({ ticketId }) => {
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();
  const fileRef = React.useRef(null);
  const messagesContainerRef = React.useRef(null);

  useEffect(() => {
    fetchTicket();
  }, [ticketId]);

  // Poll for new messages every 10 seconds
  useEffect(() => {
    if (!ticketId) return;
    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/support/tickets/${ticketId}`);
        setTicket(response.data.ticket);
        setMessages(response.data.messages);
      } catch {
        // Silently ignore polling errors
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [ticketId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const fetchTicket = async () => {
    try {
      const response = await api.get(`/support/tickets/${ticketId}`);
      setTicket(response.data.ticket);
      setMessages(response.data.messages);
    } catch (error) {
      toast.error('Failed to load ticket');
      navigate('/admin/support');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      let res;
      if (attachment) {
        const formData = new FormData();
        formData.append('message', newMessage);
        formData.append('file', attachment);
        res = await api.post(`/support/tickets/${ticketId}/messages/with-attachment`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        res = await api.post(`/support/tickets/${ticketId}/messages`, { message: newMessage });
      }
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      setAttachment(null);
      if (fileRef.current) fileRef.current.value = '';
      toast.success('Reply sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleCloseTicket = async () => {
    if (!window.confirm('Are you sure you want to close this ticket?')) return;
    try {
      await api.put(`/admin/support/tickets/${ticketId}/close`);
      toast.success('Ticket closed');
      setTicket(prev => ({ ...prev, status: 'closed' }));
    } catch (error) {
      toast.error('Failed to close ticket');
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-900"></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8">
        <button onClick={() => navigate('/admin/support')} className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Tickets
        </button>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-slate-800 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-white">{ticket?.subject}</h1>
                <p className="text-slate-300 text-sm mt-1">
                  Ticket #{ticket?.id} • From: {ticket?.user_email}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  ticket?.status === 'open' ? 'bg-blue-500 text-white' :
                  ticket?.status === 'in_progress' ? 'bg-amber-500 text-white' :
                  'bg-slate-500 text-white'
                }`}>
                  {ticket?.status?.replace('_', ' ')}
                </span>
                {ticket?.status !== 'closed' && (
                  <button
                    onClick={handleCloseTicket}
                    className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg text-sm font-medium flex items-center gap-1"
                  >
                    <XCircle className="w-4 h-4" />
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Messages */}
          <div ref={messagesContainerRef} className="p-6 max-h-[500px] overflow-y-auto space-y-4">
            {messages.map((msg) => {
              const isAdminMsg = msg.is_admin || msg.sender_role === 'admin' || msg.sender_role === 'company_admin' || msg.sender_role === 'main_admin' || msg.sender_role === 'super_admin';
              const isSuperAdmin = msg.sender_role === 'main_admin' || msg.sender_role === 'super_admin' || msg.sender_role === 'admin';
              const isCompanyAdmin = msg.sender_role === 'company_admin';

              let bubbleClasses, timestampClasses, senderLabel;
              if (isSuperAdmin) {
                bubbleClasses = 'bg-orange-100 text-orange-900';
                timestampClasses = 'text-orange-500';
                senderLabel = 'Trados Support';
              } else if (isCompanyAdmin) {
                bubbleClasses = 'bg-emerald-100 text-emerald-900';
                timestampClasses = 'text-emerald-500';
                senderLabel = 'Support';
              } else {
                bubbleClasses = 'bg-slate-100 text-slate-800';
                timestampClasses = 'text-slate-500';
                senderLabel = msg.sender_name || msg.sender_email;
              }

              return (
                <div
                  key={msg.id}
                  className={`flex ${isAdminMsg ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] rounded-lg p-4 ${bubbleClasses}`}>
                    <p className="text-sm">{msg.message}</p>
                    <AdminAttachmentDisplay attachment={msg.attachment} isAdminMsg={isAdminMsg} />
                    <p className={`text-xs mt-2 ${timestampClasses}`}>
                      {senderLabel} • {new Date(msg.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Reply Form */}
          {ticket?.status !== 'closed' && (
            <div className="p-4 border-t border-slate-200 space-y-2">
              {attachment && (
                <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 rounded px-2 py-1">
                  <Paperclip className="w-3 h-3" />
                  <span className="truncate">{attachment.name}</span>
                  <button
                    onClick={() => { setAttachment(null); if (fileRef.current) fileRef.current.value = ''; }}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <form onSubmit={handleSendMessage} className="flex gap-3">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Type your reply..."
                />
                <input
                  type="file"
                  ref={fileRef}
                  className="hidden"
                  onChange={(e) => { if (e.target.files[0]) setAttachment(e.target.files[0]); }}
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                  title="Attach file or photo"
                >
                  <Paperclip className="w-4 h-4" />
                </button>
                <button
                  type="submit"
                  disabled={sending || !newMessage.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Send
                </button>
              </form>
            </div>
          )}

          {ticket?.status === 'closed' && (
            <div className="p-4 border-t border-slate-200 bg-slate-50 text-center text-slate-500">
              This ticket has been closed.
            </div>
          )}
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminSupport;
